/**
 * Chat 域的自定义事件名 —— **与 native `analytics/ChatTrackingEvents.kt` 逐字一致**。
 *
 * ## 为什么这两条必须存在
 *
 * 「发出一条消息」与「AI 回复完成」改之前的状态：
 *
 * | 动作 | 改前 |
 * |---|---|
 * | 发消息 | 只有点击 label，**没有 payload** —— 点击消息在协议层就没有 payload 字段 |
 * | 回复完成 | **两端完全没有事件** |
 *
 * 后果是下游 `edgen_events` 区分不了「用户发完消息正在等回复」和「这轮对话已经结束」——
 * 而这恰好是「能不能插话」最关键的一个区分，此前只能靠时间猜。
 *
 * 对产品侧同样有价值：对话完成率、首字延迟、语音占比、报错率。
 *
 * ## `chat_id` 为什么在这里而不是 session metadata
 *
 * 此前 `aiChatId` 被写成 OpenReplay 的 **session metadata**（两端都绕过了各自的 §2.4 白名单）。
 * 那是错的，三条理由任一条都够：
 *
 * 1. metadata 是**会话级、只能覆盖** —— 用户在一次 session 里切会话，值会被反复改写，
 *    后台最终只留得下最后一个 chatId。**一个会中途变化的值不该做会话属性。**
 * 2. OpenReplay 自定义 metadata 只有 **10 个固定槽位**，规范规划的 18 个 key 已经超预算。
 * 3. 想按 chat 维度查询，事件字段比会话属性好用得多。
 *
 * 所以改成挂在这两条事件上。
 */
export const CHAT_EVENTS = {
  /** 用户发出一条消息。参数：`chat_id` · `has_attachment` · `attachment_count` · `is_voice` · `second_opinion`。 */
  messageSent: "chat_message_sent",
  /** 一轮 AI 回复结束（正常完成或出错）。参数：`chat_id` · `duration_ms` · `is_error` · `error_code?`。 */
  responseDone: "chat_response_done",
  /** 提问的**形态**（不含内容）。参数：`text_length` · `has_reference` · `is_slash_command`。 */
  question: "chat_question",
} as const;

/**
 * 提问的形态维度 —— **故意不含提问原文**。
 *
 * ## 为什么不发原文（改之前是 `getTracker()?.event?.("chat", text)`，payload 就是原文字符串）
 *
 * 1. **绕过门面 = 没过 `redact()`** —— 用户粘贴的 `Bearer …` / `glpat-…` / `token=…` /
 *    身份证手机号会原文进事件流，而事件流要出 Kafka 给下游。chat 就是让人粘东西的地方，
 *    这不是理论风险。
 * 2. **违反规范 §3.2**（「大对象传 id / hash **不传内容**」）**且不截断**
 *    （§5 不主动截断，超 8KB 只打 `oversize` 标记）—— 粘一份文档进来就整篇进事件流。
 * 3. **内容本来就在自己库里** —— chat 消息服务端持久化，要分析提问内容查自己的库，
 *    那才是 system of record。复制进 OpenReplay 换不来新能力：自由文本聚合不了，
 *    payload 还是字符串、连字段名都没有。
 *
 * > 「隐私」本身**不是**新增暴露 —— session 回放录的就是 DOM，用户打的字本来就看得见。
 * > 真正的区别是形态：回放是给人看的录像、按 session 存；事件 payload 是结构化可检索字段、
 * > 出 Kafka 进下游。同一份数据，后者的扩散面与留存路径完全不同。
 *
 * 这三个维度保留了分析价值（长度分布 / 带引用的占比 / slash 指令使用率），不含内容。
 */
export function buildChatQuestionPayload(text: string) {
  return {
    text_length: text.length,
    has_reference: text.includes("referenceId="),
    is_slash_command: text.startsWith("/"),
  };
}
